/* Financial-access predicates — the ONE frontend copy.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * There are now TWO ways to reach an invoice, and the browser has to tell them
 * apart or it sends people to screens that bounce them:
 *
 *   1. Financial access — the Financials module, every invoice, read + write.
 *      Mirrors backend ``authentication.permissions.HasFinancialAccess``.
 *   2. Delivery-scoped read — a trade-ops operator reading the invoices raised
 *      against a delivery they can open. READ ONLY, no module, no master data.
 *      Mirrors backend ``invoicing.delivery_scoped_access``.
 *
 * The second tier arrived on 2026-09-03 (ledger row 71) because operators were
 * shown an empty invoice panel on their own deliveries: the API refused all six
 * list calls and the panel turned the refusals into empty arrays.
 *
 * Keep these two functions in step with those two backend rules. Granting more
 * here than the backend does sends a user to a screen that 403s on load;
 * granting less silently hides something they are entitled to — the exact
 * drift that bit ``ProtectedRoute``'s Legal gate and the customer-trader
 * overlay before it (see ``customerTraderAccess.js``).
 *
 * These are UI-affordance rules, not security boundaries. Row-level scoping is
 * enforced server-side regardless of what the browser believes.
 */
import { isRestrictedCustomerTrader } from './customerTraderAccess';

/**
 * True when the user may open the Financials module and write invoices.
 *
 * Superuser / staff are included to match the backend class and
 * ``ProtectedRoute``, which both widen the same way. Missing users fail CLOSED.
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const hasFinancialAccess = (user) => Boolean(
  user && (
    user.has_full_access
    || user.has_financial_access
    || user.is_financial_approver
    || user.is_superuser
    || user.is_staff
  )
);

/**
 * True when the user reads invoices through the delivery tier instead.
 *
 * Deliberately EXCLUSIVE of ``hasFinancialAccess`` so callers can branch on one
 * or the other without deciding which wins. Customer traders are excluded:
 * they carry ``has_bunkers_access`` and can open their own fuels deliveries, so
 * the raw flag alone would let an external counterparty at our invoice book.
 *
 * Bunker Operations Approvers (2026-09-17) are in this tier too, even with no
 * Trade Ops module: checking a bunker delivery's invoices is their whole job.
 * Backend twin: ``invoicing.delivery_scoped_access.is_delivery_scoped_reader``.
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const isDeliveryScopedInvoiceReader = (user) => Boolean(
  user
  && !hasFinancialAccess(user)
  && !isRestrictedCustomerTrader(user)
  && (user.has_cargo_access || user.has_bunkers_access || user.is_bunker_ops_approver)
);

/**
 * True when the user may RAISE an invoice from a delivery they can open.
 *
 * Wider than ``hasFinancialAccess`` on purpose (Craig, 2026-09-04): the bunkers
 * or cargo operator who ran the delivery is the person who invoices it, and
 * making them wait for a Financials grant is what the live bug was. It is NOT
 * wider than that — approving, rejecting, voiding, sending and recording
 * payment stay behind ``hasFinancialAccess``, and so do the Financials lists,
 * dashboard and master data.
 *
 * Backend twin: ``invoicing.delivery_scoped_access`` admits exactly this set
 * for create / create_from_delivery / update, and clamps the delivery to the
 * ones the user can already open. This function cannot see the delivery, so it
 * answers the module-level half only; the server answers the row-level half.
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const canRaiseInvoiceFromDelivery = (user) => (
  hasFinancialAccess(user) || isDeliveryScopedInvoiceReader(user)
);

export default hasFinancialAccess;
